// src/components/setting/ActivityLog.jsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  RefreshCw, Activity, ChevronLeft, ChevronRight, X,
  Users, FileText, Calendar, Clock, Settings, ArrowRight,
  CreditCard, Shield, BarChart2, Gift, LogOut, LogIn,
  UserPlus, UserMinus, CheckCircle, XCircle, Info
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import apiClient from '../../services/apiClient';

// ── Module config ─────────────────────────────────────────────────────────────
const MODULE_CONFIG = {
  payroll:    { bg: 'bg-blue-500',    light: 'bg-blue-50 text-blue-700 border-blue-200',      icon: CreditCard,  label: 'Payroll'    },
  employees:  { bg: 'bg-emerald-500', light: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Users,   label: 'Employees' },
  attendance: { bg: 'bg-amber-500',   light: 'bg-amber-50 text-amber-700 border-amber-200',   icon: Calendar,    label: 'Attendance' },
  leave:      { bg: 'bg-purple-500',  light: 'bg-purple-50 text-purple-700 border-purple-200', icon: FileText,   label: 'Leave'      },
  advances:   { bg: 'bg-orange-500',  light: 'bg-orange-50 text-orange-700 border-orange-200', icon: Gift,       label: 'Advances'   },
  holidays:   { bg: 'bg-rose-500',    light: 'bg-rose-50 text-rose-700 border-rose-200',       icon: Calendar,   label: 'Holidays'   },
  team:       { bg: 'bg-cyan-500',    light: 'bg-cyan-50 text-cyan-700 border-cyan-200',       icon: UserPlus,   label: 'Team'       },
  settings:   { bg: 'bg-slate-500',   light: 'bg-slate-100 text-slate-700 border-slate-200',   icon: Settings,   label: 'Settings'   },
  auth:       { bg: 'bg-indigo-500',  light: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Shield,     label: 'Auth'       },
  reports:    { bg: 'bg-teal-500',    light: 'bg-teal-50 text-teal-700 border-teal-200',       icon: BarChart2,  label: 'Reports'    },
};

const MODULES = Object.keys(MODULE_CONFIG);

const relativeTime = (date) => {
  const diff  = Date.now() - new Date(date).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)   return `${days}d ago`;
  return new Date(date).toLocaleDateString('en-IN', { day:'numeric', month:'short' });
};

// ── Detail Modal ──────────────────────────────────────────────────────────────
function LogDetailModal({ log, onClose }) {
  if (!log) return null;
  const mod = MODULE_CONFIG[log.module] || MODULE_CONFIG.settings;
  const ModIcon = mod.icon;
  const hasChanges = log.changes && log.changes.length > 0;

  return (
    <Dialog open={!!log} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className={`px-6 pt-5 pb-4 border-b border-slate-200`}>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${mod.light.split(' ').find(c=>c.startsWith('bg-'))} border ${mod.light.split(' ').find(c=>c.startsWith('border-'))}`}>
              <ModIcon className={`w-5 h-5 ${mod.light.split(' ').find(c=>c.startsWith('text-'))}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${mod.light}`}>
                  {mod.label}
                </span>
                {log.status === 'error' && (
                  <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-md">Failed</span>
                )}
              </div>
              <DialogTitle className="text-base font-semibold text-slate-900 mt-1 font-heading">
                {log.description}
                {log.resourceName && <span className="text-slate-500 font-normal ml-1">— {log.resourceName}</span>}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                <Clock className="w-3 h-3" />
                <span>{new Date(log.createdAt).toLocaleString('en-IN', { dateStyle:'long', timeStyle:'short' })}</span>
                <span className="text-slate-200">·</span>
                <span>{relativeTime(log.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* Who */}
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 font-semibold">Performed by</div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center text-white text-xs font-semibold">
                {(log.userName || log.userEmail || '?').slice(0,2).toUpperCase()}
              </div>
              <div>
                <div className="text-sm font-medium text-slate-900">{log.userName || '—'}</div>
                <div className="text-xs text-slate-400">{log.userEmail}</div>
              </div>
            </div>
          </div>

          {/* Changes — before/after */}
          {hasChanges && (
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-3 font-semibold">
                Changes Made ({log.changes.length} field{log.changes.length !== 1 ? 's' : ''})
              </div>
              <div className="space-y-2">
                {log.changes.map((c, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 overflow-hidden">
                    {/* Field name */}
                    <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200">
                      <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">{c.field}</span>
                    </div>
                    {/* Before → After */}
                    <div className="flex items-center divide-x divide-slate-100">
                      {c.before ? (
                        <>
                          <div className="flex-1 px-3 py-2.5 bg-red-50/50">
                            <div className="text-[10px] text-red-400 font-medium uppercase tracking-wider mb-0.5">Before</div>
                            <div className="text-sm text-red-700 font-medium line-through decoration-red-300">{c.before}</div>
                          </div>
                          <div className="px-2 py-2.5 flex items-center bg-white">
                            <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                          </div>
                          <div className="flex-1 px-3 py-2.5 bg-emerald-50/50">
                            <div className="text-[10px] text-emerald-500 font-medium uppercase tracking-wider mb-0.5">After</div>
                            <div className="text-sm text-emerald-800 font-semibold">{c.after}</div>
                          </div>
                        </>
                      ) : (
                        <div className="flex-1 px-3 py-2.5">
                          <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-0.5">Value</div>
                          <div className="text-sm text-slate-800 font-semibold">{c.after}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No changes notice */}
          {!hasChanges && (
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <Info className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <p className="text-xs text-slate-500">
                  {log.action === 'LOGOUT' || log.action === 'LOGIN'
                    ? 'No data changes — this is a session event.'
                    : log.action === 'VIEW_REPORT'
                    ? 'Report was viewed. No data was modified.'
                    : 'No change details available for this event.'}
                </p>
              </div>
            </div>
          )}

          {/* Technical details */}
          <div className="px-6 py-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 font-semibold">Technical Details</div>
            <div className="space-y-1.5 text-xs">
              {[
                ['Action', log.action],
                ['Method', log.method],
                ['Path',   log.path?.split('?')[0]],
                ['IP',     log.ip === '::ffff:172.19.0.1' ? 'localhost' : log.ip],
                ['Status', log.status],
              ].map(([k, v]) => v && (
                <div key={k} className="flex gap-3">
                  <span className="text-slate-400 w-14 flex-shrink-0">{k}</span>
                  <span className="font-mono text-slate-700">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Log row ───────────────────────────────────────────────────────────────────
function LogRow({ log, onSelect, isLast }) {
  const mod = MODULE_CONFIG[log.module] || MODULE_CONFIG.settings;
  const ModIcon = mod.icon;
  const hasChanges = log.changes && log.changes.length > 0;

  return (
    <button
      onClick={() => onSelect(log)}
      className={`w-full text-left flex items-start gap-3 px-5 py-3.5 hover:bg-slate-50 transition-colors group ${!isLast ? 'border-b border-slate-100' : ''}`}
    >
      {/* Icon */}
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 border ${mod.light.split(' ').find(c=>c.startsWith('bg-'))} ${mod.light.split(' ').find(c=>c.startsWith('border-'))}`}>
        <ModIcon className={`w-3.5 h-3.5 ${mod.light.split(' ').find(c=>c.startsWith('text-'))}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${mod.light}`}>
            {mod.label}
          </span>
          <span className="text-sm font-semibold text-slate-800">{log.description}</span>
          {log.resourceName && (
            <span className="text-xs text-slate-400 truncate max-w-[120px]">— {log.resourceName}</span>
          )}
          {log.status === 'error' && (
            <span className="text-[10px] text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded flex items-center gap-0.5">
              <XCircle className="w-2.5 h-2.5" /> Failed
            </span>
          )}
        </div>

        {/* Change pills preview */}
        {hasChanges && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {log.changes.slice(0, 3).map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 rounded-md px-2 py-0.5 text-slate-600">
                <span className="text-slate-400">{c.field}:</span>
                <span className="font-semibold text-slate-800 max-w-[80px] truncate">{c.after}</span>
              </span>
            ))}
            {log.changes.length > 3 && (
              <span className="text-[11px] text-slate-400 self-center">+{log.changes.length - 3} more</span>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400">
          <Clock className="w-3 h-3 flex-shrink-0" />
          <span title={new Date(log.createdAt).toLocaleString('en-IN')}>{relativeTime(log.createdAt)}</span>
          <span className="text-slate-200">·</span>
          <span>{new Date(log.createdAt).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })}</span>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2 flex-shrink-0 pl-2">
        <span className="text-[9px] font-mono text-slate-300 hidden lg:block">{log.action}</span>
        <div className="w-5 h-5 rounded-md border border-slate-200 bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="w-3 h-3 text-slate-400" />
        </div>
      </div>
    </button>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ActivityLog() {
  const [page,        setPage]       = useState(1);
  const [module,      setModule]     = useState('');
  const [startDate,   setStartDate]  = useState('');
  const [endDate,     setEndDate]    = useState('');
  const [selectedLog, setSelectedLog]= useState(null);

  const { data: rawData, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['activityLogs', page, module, startDate, endDate],
    queryFn: async () => {
      const params = { page, limit: 20 };
      if (module)    params.module    = module;
      if (startDate) params.startDate = startDate;
      if (endDate)   params.endDate   = endDate;
      const res = await apiClient.get('/api/activity', { params });
      return res.data;
    },
  });

  const logs  = rawData?.data?.logs  || [];
  const total = rawData?.data?.total || 0;
  const pages = rawData?.data?.pages || 1;
  const hasFilters = !!(module || startDate || endDate);

  return (
    <div className="space-y-4">

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label:'Total Events',   value: total,  color:'text-slate-900'  },
          { label:'Success',        value: logs.filter(l=>l.status==='success').length, color:'text-emerald-600' },
          { label:'Errors',         value: logs.filter(l=>l.status==='error').length,   color:'text-red-500'    },
          { label:'Modules Active', value: [...new Set(logs.map(l=>l.module))].length,  color:'text-[#2E6DA4]'  },
        ].map(s => (
          <Card key={s.label} className="p-4 border-slate-200 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{s.label}</div>
            <div className={`text-2xl font-bold font-mono mt-1 ${s.color}`}>{isLoading ? '…' : s.value}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-4 border-slate-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 items-end flex-wrap">
          <div className="space-y-1 min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Module</Label>
            <Select value={module || 'all'} onValueChange={v => { setModule(v==='all'?'':v); setPage(1); }}>
              <SelectTrigger className="h-9 bg-white"><SelectValue placeholder="All modules" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {MODULES.map(m => (
                  <SelectItem key={m} value={m}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${MODULE_CONFIG[m].bg}`} />
                      <span className="capitalize">{m}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
            <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} className="h-9 w-36 bg-white" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
            <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} className="h-9 w-36 bg-white" />
          </div>
          <div className="flex gap-2 items-end">
            {hasFilters && <Button variant="outline" size="sm" className="h-9" onClick={() => { setModule(''); setStartDate(''); setEndDate(''); setPage(1); }}>Clear</Button>}
            <Button variant="outline" size="sm" className="h-9" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isFetching?'animate-spin':''}`} /> Refresh
            </Button>
          </div>
        </div>
        {total > 0 && (
          <p className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-400">
            {total} event{total!==1?'s':''}{hasFilters?' (filtered)':''}
          </p>
        )}
      </Card>

      {/* Timeline */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#2E6DA4]" />
            <h3 className="text-sm font-semibold text-slate-900">Activity Timeline</h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            {isFetching && !isLoading && <RefreshCw className="w-3 h-3 animate-spin" />}
            <span>Click any row for full details</span>
          </div>
        </div>

        {isLoading ? (
          <div className="py-16 text-center">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-400">Loading activity…</p>
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <XCircle className="w-8 h-8 text-red-200 mx-auto mb-3" />
            <p className="text-sm text-red-400">Failed to load. Try refreshing.</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center mx-auto mb-4">
              <Activity className="w-6 h-6 text-slate-300" />
            </div>
            <p className="text-sm font-medium text-slate-500">No activity found</p>
            <p className="text-xs text-slate-400 mt-1">
              {hasFilters ? 'Try adjusting your filters.' : 'Actions like running payroll and editing employees will appear here.'}
            </p>
          </div>
        ) : (
          logs.map((log, i) => (
            <LogRow key={log._id || i} log={log} onSelect={setSelectedLog} isLast={i===logs.length-1} />
          ))
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50/50">
            <span className="text-xs text-slate-500">Page {page} of {pages}</span>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setPage(p=>p-1)} disabled={page<=1}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setPage(p=>p+1)} disabled={page>=pages}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail modal */}
      <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
}